import {
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import { toAuditPageError } from './AuditLogPage';
import { auditRepositoryToken, defaultSecurityEventQuery } from './repository';
import type {
  AuditLogRole,
  SecurityEvent,
  SecurityEventFilters,
  SecurityEventQuery,
  SecurityEventSeverity,
} from './types';

const roleOptions: readonly AuditLogRole[] = [
  'SUPER_ADMIN',
  'AGENT_SUPER_USER',
  'AGENT_ADMIN_USER',
];
const severityOptions: readonly SecurityEventSeverity[] = ['INFO', 'WARNING', 'HIGH', 'CRITICAL'];

export function SecurityEventsPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(auditRepositoryToken);
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [query, setQuery] = useState<SecurityEventQuery>(() => initialQuery(searchParams));
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const resource = useResource(
    (signal) => repository.listSecurityEvents(query, signal),
    [query, refresh, repository],
    (value) => value.items.length === 0,
  );
  const reset = (filters: SecurityEventFilters) => {
    setHistory(['']);
    setPage(0);
    setSearchParams({}, { replace: true });
    setQuery({ ...query, cursor: '', filters });
  };
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('securityEvents.subtitle')}
        title={t('securityEvents.title')}
      />
      <SecurityEventFiltersForm onApply={reset} value={query.filters} />
      <PageState
        emptyMessage={t('securityEvents.empty')}
        error={toAuditPageError(resource.error, t('securityEvents.errors.loadList'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <>
            {mobile ? (
              <CompactSecurityEventResults items={resource.data.items} language={language} />
            ) : (
              <TableRegion label={t('securityEvents.table')}>
                <table aria-label={t('securityEvents.table')}>
                  <thead>
                    <tr>
                      <th>{t('securityEvents.fields.id')}</th>
                      <th>{t('securityEvents.fields.category')}</th>
                      <th>{t('securityEvents.fields.severity')}</th>
                      <th>{t('securityEvents.fields.eventCode')}</th>
                      <th>{t('securityEvents.fields.actor')}</th>
                      <th>{t('securityEvents.fields.occurredAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resource.data.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <Link to={`${paths.securityEvents}/${encodeURIComponent(item.id)}`}>
                            {item.id}
                          </Link>
                        </td>
                        <td>{item.category}</td>
                        <td>{item.severity}</td>
                        <td>{item.eventCode}</td>
                        <td>
                          {item.principalActorId ?? t('common.notAvailable')} ·{' '}
                          {item.principalRole ?? t('common.notAvailable')}
                        </td>
                        <td>
                          {formatTimestamp(item.occurredAt, 'Asia/Bangkok', language).display}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableRegion>
            )}
            {resource.data.requestId === null ? null : (
              <Typography variant="caption">
                {t('securityEvents.fields.requestId')}: {resource.data.requestId}
              </Typography>
            )}
            <CursorActions
              hasNext={resource.data.nextCursor !== null}
              page={page}
              onNext={() => {
                const cursor = resource.data?.nextCursor;
                if (cursor === null || cursor === undefined) return;
                const index = page + 1;
                setHistory((current) => [...current.slice(0, index), cursor]);
                setPage(index);
                setQuery((current) => ({ ...current, cursor }));
              }}
              onPrevious={() => {
                const index = page - 1;
                setPage(index);
                setQuery((current) => ({ ...current, cursor: history[index] ?? '' }));
              }}
            />
          </>
        )}
      </PageState>
    </Stack>
  );
}

function SecurityEventFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: SecurityEventFilters) => void;
  readonly value: SecurityEventFilters;
}) {
  const initialValue = useRef(value);
  const { register, handleSubmit, reset } = useForm<SecurityEventFilters>({
    defaultValues: initialValue.current,
  });
  const { t } = useTranslation();
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      principalRole: value.principalRole,
      partnerId: value.partnerId,
      agentId: value.agentId,
      credentialActorId: value.credentialActorId,
      requestId: value.requestId,
      sourceIp: value.sourceIp,
      category: value.category,
      to: value.to,
    },
    {
      principalRole: defaultSecurityEventQuery.filters.principalRole,
      partnerId: defaultSecurityEventQuery.filters.partnerId,
      agentId: defaultSecurityEventQuery.filters.agentId,
      credentialActorId: defaultSecurityEventQuery.filters.credentialActorId,
      requestId: defaultSecurityEventQuery.filters.requestId,
      sourceIp: defaultSecurityEventQuery.filters.sourceIp,
      category: defaultSecurityEventQuery.filters.category,
      to: defaultSecurityEventQuery.filters.to,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      ariaLabel={t('filterPanel.label', { page: t('securityEvents.title') })}
      onClear={() => {
        reset(defaultSecurityEventQuery.filters);
        onApply(defaultSecurityEventQuery.filters);
      }}
      onSubmit={handleSubmit(onApply)}
      primaryFields={
        <>
          <TextField
            label={t('securityEvents.fields.principalActorId')}
            size="small"
            {...register('principalActorId')}
          />
          <TextField
            defaultValue={initialValue.current.severity}
            label={t('securityEvents.fields.severity')}
            select
            size="small"
            {...register('severity')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {severityOptions.map((severity) => (
              <MenuItem key={severity} value={severity}>
                {severity}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('securityEvents.fields.eventCode')}
            size="small"
            {...register('eventCode')}
          />
          <TextField
            label={t('securityEvents.fields.from')}
            size="small"
            type="datetime-local"
            {...register('from')}
          />
        </>
      }
      secondaryFields={
        <>
          <TextField
            defaultValue={initialValue.current.principalRole}
            label={t('securityEvents.fields.principalRole')}
            select
            size="small"
            {...register('principalRole')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('securityEvents.fields.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField
            label={t('securityEvents.fields.agentId')}
            size="small"
            {...register('agentId')}
          />
          <TextField
            label={t('securityEvents.fields.credentialActorId')}
            size="small"
            {...register('credentialActorId')}
          />
          <TextField
            label={t('securityEvents.fields.requestId')}
            size="small"
            {...register('requestId')}
          />
          <TextField
            label={t('securityEvents.fields.sourceIp')}
            size="small"
            {...register('sourceIp')}
          />
          <TextField
            label={t('securityEvents.fields.category')}
            size="small"
            {...register('category')}
          />
          <TextField
            label={t('securityEvents.fields.to')}
            size="small"
            type="datetime-local"
            {...register('to')}
          />
        </>
      }
    />
  );
}

function CompactSecurityEventResults({
  items,
  language,
}: {
  readonly items: readonly SecurityEvent[];
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('securityEvents.compact')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {items.map((item) => (
        <Paper component="li" key={item.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <Link to={`${paths.securityEvents}/${encodeURIComponent(item.id)}`}>{item.id}</Link>
            <Typography variant="body2">
              {item.category} · {item.severity} · {item.eventCode}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {formatTimestamp(item.occurredAt, 'Asia/Bangkok', language).display}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function TableRegion({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label: string;
}) {
  return (
    <Stack
      aria-label={label}
      role="region"
      tabIndex={0}
      sx={{
        overflowX: 'auto',
        '& table': { borderCollapse: 'collapse', minWidth: 900, width: '100%' },
        '& th, & td': { borderBottom: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
      }}
    >
      {children}
    </Stack>
  );
}

function CursorActions({
  hasNext,
  onNext,
  onPrevious,
  page,
}: {
  readonly hasNext: boolean;
  readonly onNext: () => void;
  readonly onPrevious: () => void;
  readonly page: number;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      <Button disabled={page === 0} onClick={onPrevious}>
        {t('securityEvents.pagination.previous')}
      </Button>
      <Button disabled={!hasNext} onClick={onNext}>
        {t('securityEvents.pagination.next')}
      </Button>
    </Stack>
  );
}

function initialQuery(params: URLSearchParams): SecurityEventQuery {
  return {
    ...defaultSecurityEventQuery,
    filters: {
      principalActorId: validText(params.get('principalActorId')),
      principalRole: allow(params.get('principalRole'), roleOptions),
      partnerId: validText(params.get('partnerId')),
      agentId: validText(params.get('agentId')),
      requestId: validText(params.get('requestId')),
      sourceIp: validText(params.get('sourceIp')),
      from: validTime(params.get('from')),
      to: validTime(params.get('to')),
      credentialActorId: validText(params.get('credentialActorId')),
      category: normalizeCode(params.get('category') ?? ''),
      severity: allow(params.get('severity'), severityOptions),
      eventCode: normalizeCode(params.get('eventCode') ?? ''),
    },
  };
}

function allow<const Value extends string>(
  value: string | null,
  values: readonly Value[],
): Value | '' {
  return value !== null && values.includes(value as Value) ? (value as Value) : '';
}

function normalizeCode(value: string): string {
  const code = value.trim().toUpperCase();
  return /^[A-Z][A-Z0-9_]{1,99}$/u.test(code) ? code : '';
}

function validTime(value: string | null): string {
  if (
    value === null ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:\d{2})?$/u.test(value)
  )
    return '';
  try {
    formatTimestamp(bangkokWallTimeToRfc3339(value));
    return value;
  } catch {
    return '';
  }
}

function validText(value: string | null): string {
  return value !== null && value.trim().length > 0 ? value.trim() : '';
}
